import { encryptedRequest, CoopApiError } from './coopApi';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const TOKEN_KEY = 'coop_session_token';
let redirectingToLogin = false;

function normalizeQuery(params = {}) {
  const query = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    // coop-api 当前的 relay query 协议是 key/value 对。数组请求应优先放入 JSON body；
    // 对只支持 GET 的兼容接口，使用逗号表达以避免丢失参数。
    query[key] = Array.isArray(value) ? value.join(',') : String(value);
  });
  return query;
}

function splitUrl(url) {
  const [path, rawQuery = ''] = url.split('?');
  const query = {};
  new URLSearchParams(rawQuery).forEach((value, key) => {
    query[key] = value;
  });
  return { path, query };
}

function serializeBody(data) {
  if (data === undefined) return undefined;
  if (data instanceof FormData || data instanceof Blob || data instanceof File) {
    throw new CoopApiError('加密中继不支持 FormData；请使用 /v1/upload/sign 或分片上传接口');
  }
  return JSON.stringify(data);
}

function stripUnsafeHeaders(headers = {}) {
  const safeHeaders = {};
  Object.entries(headers).forEach(([key, value]) => {
    const normalized = key.toLowerCase();
    // 身份由 coop session 在服务端解析并注入，前端不能透传上游认证或内部 Token。
    if (normalized === 'authorization' || normalized === 'x-internal-token') return;
    safeHeaders[key] = String(value);
  });
  return safeHeaders;
}

export function bootstrapTokenFromUrl() {
  // 不再接受 URL token：coop 会话不应暴露在 URL、浏览器历史或 Referer 中。
}

export function getToken() {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setToken(token) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(TOKEN_KEY);
  // 清理此前直连 admanager 的令牌，避免旧 token 被后续代码意外使用。
  localStorage.removeItem('access_token');
  localStorage.removeItem('token');
  localStorage.removeItem('Authorization');
  localStorage.removeItem('userInfo');
  // 清理缓存的 admanager 组织/角色上下文
  localStorage.removeItem('admanager_user_context');
}

function redirectToLogin() {
  if (redirectingToLogin || typeof window === 'undefined') return;

  redirectingToLogin = true;
  removeToken();

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (window.location.pathname !== '/login') {
    localStorage.setItem('path', currentPath);
    window.location.href = '/login';
  }
}

function isUnauthorizedPayload(payload) {
  return Number(payload?.code) === 401 || Number(payload?.status) === 401;
}

export function transformResponseData(data) {
  return data;
}

// ---------- 重复请求去重 / 自动取消 ----------
// 行为与旧版 ad-tools-vben（legacy-ad-src/utils/request.js）保持一致：
// - 默认只对 GET 同路径请求做"后来的顶替先来的"自动取消，避免快速点击导致结果回跳；
// - 传 cancelKey 可自定义分组 key（任意方法都会参与自动取消，用于把不同接口/页面归到一组）；
// - 传 noCancel:true 的接口允许短时间重复/并发请求（如状态轮询、批量操作重试），不参与自动取消；
// - 调用方自带 signal 时视为自行管理取消，不参与自动取消。
const pendingMap = new Map();

function normalizeCancelPath(path = '') {
  return String(path).split('?')[0].replace(/\/+$/, '') || String(path);
}

function getRequestKey(method = 'get', path = '', cancelKey) {
  if (cancelKey != null && cancelKey !== '') return String(cancelKey);
  return `${(method || 'get').toLowerCase()}:${normalizeCancelPath(path)}`;
}

function shouldAutoCancel(options = {}) {
  if (options.noCancel || options.signal) return false;
  if (options.cancelKey != null && options.cancelKey !== '') return true;
  return (options.method || 'get').toLowerCase() === 'get';
}

/**
 * 手动取消进行中的请求（与旧版 cancelPendingRequests 对齐）。
 * - 传入 cancelKey 只取消该分组下的请求；
 * - 不传参数则取消所有进行中的请求。
 */
export function cancelPendingRequests(cancelKey) {
  if (cancelKey != null && cancelKey !== '') {
    const controller = pendingMap.get(String(cancelKey));
    if (controller) {
      controller.abort();
      pendingMap.delete(String(cancelKey));
    }
    return;
  }
  pendingMap.forEach((controller) => controller.abort());
  pendingMap.clear();
}

/**
 * 兼容现有 service 层：所有 /api/* 调用会被封装为 /v1/relay 加密请求。
 * 浏览器仅保存 coop session，admanager Bearer Token 永不下发至前端。
 */
export async function request(url, options = {}) {
  // 仅本地 mock 模式（VITE_USE_MOCK=true）才动态加载 mock；正式/测试构建 USE_MOCK=false，
  // 该分支被 tree-shake 掉，产物中不包含任何 mock 代码与数据。
  if (USE_MOCK && url.startsWith('/api/')) {
    const { mockRequest } = await import('./adToolsMock');
    return mockRequest(url, options);
  }

  const { method = 'get', params, data, headers = {}, responseType = 'json', silentError = false, signal, noCancel, cancelKey } = options;
  const { path, query: urlQuery } = splitUrl(url);
  if (!path.startsWith('/api/')) {
    throw new CoopApiError(`不支持的接口路径：${path}`);
  }

  // 重复请求去重：同 key 请求仍在前置时，终结前置请求，由本次请求接管。
  const autoCancel = shouldAutoCancel({ method, signal, noCancel, cancelKey });
  const cancelGroupKey = autoCancel ? getRequestKey(method, path, cancelKey) : null;
  let cancelController = null;
  if (cancelGroupKey) {
    const previous = pendingMap.get(cancelGroupKey);
    if (previous) {
      previous.abort();
      pendingMap.delete(cancelGroupKey);
    }
    cancelController = new AbortController();
    pendingMap.set(cancelGroupKey, cancelController);
  }
  const requestSignal = cancelController ? cancelController.signal : signal;

  const body = serializeBody(data);
  const relayHeaders = {
    Accept: responseType === 'text' ? 'text/plain' : 'application/json',
    'Accept-Language': 'zh-CN',
    ...(body === undefined ? {} : { 'Content-Type': 'application/json;charset=UTF-8' }),
    ...stripUnsafeHeaders(headers)
  };

  let payload;
  try {
    const result = await encryptedRequest(
      '/v1/relay',
      {
        method: method.toUpperCase(),
        path,
        query: { ...urlQuery, ...normalizeQuery(params) },
        headers: relayHeaders,
        ...(body === undefined ? {} : { body }),
        ...(getToken() ? { session_token: getToken() } : {})
      },
      { responseType, signal: requestSignal }
    );
    payload = result.body;
  } catch (error) {
    // 被新请求顶替/手动终结的请求视为取消，不属于失败：不触发登出与错误提示。
    if (error?.isCancel) throw error;
    // coop 会话失效或上游返回 401 时统一回登录页（与旧前端行为一致）。
    if (error?.status === 401 || isUnauthorizedPayload(error?.payload)) {
      redirectToLogin();
    }
    if (silentError) return error.payload;
    throw error;
  } finally {
    // 仅当该 key 仍登记的是本次请求的 controller 时才移除，
    // 避免被后续请求顶替后由旧请求误删新请求的登记。
    if (cancelGroupKey && cancelController && pendingMap.get(cancelGroupKey) === cancelController) {
      pendingMap.delete(cancelGroupKey);
    }
  }

  if (responseType === 'text') return payload;
  if (payload?.code !== undefined && Number(payload.code) !== 0) {
    if (isUnauthorizedPayload(payload)) {
      redirectToLogin();
    }
    const error = new CoopApiError(payload?.message || '请求失败', { payload, status: payload.code });
    if (!silentError) throw error;
  }
  return transformResponseData(payload);
}

export const apiEnv = import.meta.env.VITE_API_ENV || 'development';
